import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import type { SparklinePoint } from "@/components/ui/sparkline";
import { cn } from "@/lib/utils";

/**
 * TrendSparklineCard — Phase 17, lesbarkeit-überarbeitung (Rolle C, #3).
 *
 * Period-picker (7/14/30d) triggert nur diese query (eigene cache-key),
 * status-card refetcht nicht.
 *
 * Lesbarkeit-redesign gegenüber der reinen inline-Sparkline:
 * Der verlauf stand vorher als nackte polyline auf weißem card-grund —
 * ohne maßstab, ohne achsen, kontrast-arm. Jetzt eigener TrendChart mit
 *   - sunken plot-fläche (--color-surface-sunken) als kontrast-container,
 *   - 3 horizontale gridlines + min/max-temp-labels (Y-orientierung),
 *   - X-achsen-datums-labels (erstes + letztes datum),
 *   - kräftigerer stroke (text-primary, 2px) für ablesbarkeit,
 *   - always-on peak-annotation (datum + stufe) statt nur hover-tooltip.
 * Color-blind-safety bleibt: kurven-form = primärsignal, heat-farbe der
 * punkte = sekundär; jeder punkt trägt zusätzlich <title> mit klartext.
 *
 * Die geteilte components/ui/sparkline.tsx bleibt unangetastet (wird auch
 * im träger-vergleich genutzt) — diese reichere darstellung ist
 * dashboard-lokal.
 *
 * Empty-state bei <2 datenpunkten. Loading-state: pulse-placeholder
 * gleicher größe.
 */

type Period = "7d" | "14d" | "30d";

interface TrendResponse {
  period: Period;
  points: SparklinePoint[];
}

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
  { value: "7d", label: "7 Tage" },
  { value: "14d", label: "14 Tage" },
  { value: "30d", label: "30 Tage" },
];

export function TrendSparklineCard(): React.ReactElement {
  const [period, setPeriod] = React.useState<Period>("7d");

  const trend = useQuery({
    queryKey: ["dashboard", "trend", period],
    queryFn: () => apiFetch<TrendResponse>(`/v1/dashboard/trend?period=${period}`),
    staleTime: 60_000,
  });

  return (
    <article className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-6 shadow-[var(--shadow-md)]">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-[var(--letter-spacing-wide)] text-[color:var(--color-text-secondary)]">
          Verlauf
        </h2>
        <fieldset className="flex flex-wrap gap-1.5">
          <legend className="sr-only">Zeitraum</legend>
          {PERIOD_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={cn(
                "cursor-pointer rounded-md border px-2.5 py-1 text-xs font-medium",
                period === opt.value
                  ? "border-[color:var(--color-warm-orange-700)] bg-[color:var(--color-warm-orange-50)] text-[color:var(--color-warm-orange-900)]"
                  : "border-[color:var(--color-border-strong)] bg-[color:var(--color-surface)] text-[color:var(--color-text-secondary)] hover:bg-[color:var(--color-surface-sunken)]",
              )}
            >
              <input
                type="radio"
                name="trend-period"
                value={opt.value}
                checked={period === opt.value}
                onChange={() => setPeriod(opt.value)}
                className="sr-only"
              />
              {opt.label}
            </label>
          ))}
        </fieldset>
      </header>

      <div className="mt-4">
        {trend.isPending && (
          <div className="h-40 animate-pulse rounded-lg bg-[color:var(--color-surface-sunken)]" />
        )}
        {trend.isError && (
          <p className="text-sm text-[color:var(--color-heat-4)]">
            Verlauf konnte nicht geladen werden.{" "}
            <button
              type="button"
              onClick={() => void trend.refetch()}
              className="underline"
            >
              Erneut versuchen
            </button>
          </p>
        )}
        {trend.data && trend.data.points.length >= 2 ? (
          <TrendChart data={trend.data.points} />
        ) : trend.data ? (
          <p className="text-sm text-[color:var(--color-text-secondary)]">
            Zu wenig Messdaten — bitte Einträge nach 2–3 Tagen wieder ansehen.
          </p>
        ) : null}
      </div>
    </article>
  );
}

TrendSparklineCard.displayName = "TrendSparklineCard";

/* ─────────────────────────────────────────────────────────────────────────
 * TrendChart — lesbarkeits-optimierte verlauf-darstellung (#3).
 *
 * SVG-koordinaten in einem festen viewBox; preserveAspectRatio="none" wäre
 * hier falsch (würde text + punkte verzerren), darum skaliert das SVG über
 * width="100%" + height-attribut bei erhaltenem seitenverhältnis. Stroke +
 * punkt-radien sind in viewBox-einheiten gewählt, sodass sie bei der
 * ziel-darstellungsbreite (~280-640px) optisch konsistent bleiben.
 * ───────────────────────────────────────────────────────────────────── */

type HeatLevel = 0 | 1 | 2 | 3 | 4;

const LEVEL_FILL: Record<HeatLevel, string> = {
  0: "var(--color-text-secondary)",
  1: "var(--color-heat-1)",
  2: "var(--color-heat-2)",
  3: "var(--color-heat-3)",
  4: "var(--color-heat-4)",
};

const LEVEL_LABEL: Record<HeatLevel, string> = {
  0: "unauffällig",
  1: "normal",
  2: "soll",
  3: "muss",
  4: "kritisch",
};

// viewBox-geometrie. Padding lässt platz für Y-labels (links) + X-labels
// + peak-annotation (oben/unten).
const VIEW_W = 560;
const VIEW_H = 200;
const PAD_LEFT = 44; // raum für Y-temp-labels
const PAD_RIGHT = 12;
const PAD_TOP = 26; // raum für peak-annotation
const PAD_BOTTOM = 22; // raum für X-datum-labels
const GRID_LINES = 3; // inkl. oberer + unterer kante → 3 ⇒ min, mitte, max

const tooltipDateFormatter = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
});

const tempFormatter = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const tempFormatterAxis = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function clampLevel(n: number): HeatLevel {
  if (n <= 0) return 0;
  if (n >= 4) return 4;
  return n as HeatLevel;
}

function TrendChart({ data }: { data: SparklinePoint[] }): React.ReactElement | null {
  if (data.length < 2) return null;

  const temps = data.map((p) => p.avg_temp_c);
  const rawMin = Math.min(...temps);
  const rawMax = Math.max(...temps);
  // 1°-puffer oben+unten, damit punkte nicht auf der achse kleben.
  const minTemp = rawMin - 1;
  const maxTemp = rawMax + 1;
  const tempRange = maxTemp - minTemp || 1;

  const innerW = VIEW_W - PAD_LEFT - PAD_RIGHT;
  const innerH = VIEW_H - PAD_TOP - PAD_BOTTOM;
  const stepX = innerW / (data.length - 1);

  const yFor = (temp: number) => PAD_TOP + innerH - ((temp - minTemp) / tempRange) * innerH;

  const points = data.map((p, i) => ({
    x: PAD_LEFT + i * stepX,
    y: yFor(p.avg_temp_c),
    level: clampLevel(p.max_level),
    date: p.date,
    temp: p.avg_temp_c,
  }));

  const polyline = points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  // Gridlines: gleichmäßig über den temp-bereich verteilt, jeweils mit
  // temp-label am linken rand (orientierung in °C).
  const gridRows = Array.from({ length: GRID_LINES }, (_, i) => {
    const t = maxTemp - (tempRange * i) / (GRID_LINES - 1);
    return { temp: t, y: yFor(t) };
  });

  const peak = data.reduce((acc, p) => (p.max_level > acc.max_level ? p : acc), data[0]!);
  const peakIdx = data.indexOf(peak);
  const peakPoint = points[peakIdx]!;
  const peakDateLabel = tooltipDateFormatter.format(new Date(peak.date));
  const peakLevel = clampLevel(peak.max_level);

  const firstDate = tooltipDateFormatter.format(new Date(data[0]!.date));
  const lastDate = tooltipDateFormatter.format(new Date(data[data.length - 1]!.date));

  const ariaLabel =
    `Verlauf ${data.length} Tage, ${firstDate} bis ${lastDate}. ` +
    `Höchste Stufe ${peakLevel} (${LEVEL_LABEL[peakLevel]}) am ${peakDateLabel} ` +
    `bei ${tempFormatter.format(peak.avg_temp_c)} Grad Celsius. ` +
    `Temperaturbereich ${tempFormatterAxis.format(rawMin)} bis ${tempFormatterAxis.format(rawMax)} Grad.`;

  // Peak-annotation horizontal innerhalb des plots halten (anchor flippt
  // an den rändern, damit der text nicht abgeschnitten wird).
  const peakAnchor: "start" | "middle" | "end" =
    peakPoint.x < PAD_LEFT + 40 ? "start" : peakPoint.x > VIEW_W - PAD_RIGHT - 40 ? "end" : "middle";

  return (
    <figure className="m-0">
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        width="100%"
        className="block h-auto w-full"
        style={{ aspectRatio: `${VIEW_W} / ${VIEW_H}` }}
      >
        {/* Sunken plot-fläche als kontrast-container */}
        <rect
          x={PAD_LEFT}
          y={PAD_TOP}
          width={innerW}
          height={innerH}
          rx={8}
          fill="var(--color-surface-sunken)"
        />

        {/* Horizontale gridlines + Y-temp-labels */}
        {gridRows.map((g, i) => {
          const isEdge = i === 0 || i === gridRows.length - 1;
          return (
            <g key={`grid-${i}`}>
              <line
                x1={PAD_LEFT}
                y1={g.y}
                x2={VIEW_W - PAD_RIGHT}
                y2={g.y}
                stroke="var(--color-border-strong)"
                strokeWidth={1}
                strokeDasharray={isEdge ? undefined : "3 4"}
                opacity={isEdge ? 0.9 : 0.6}
              />
              <text
                x={PAD_LEFT - 8}
                y={g.y}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={12}
                fill="var(--color-text-secondary)"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {tempFormatterAxis.format(g.temp)}°
              </text>
            </g>
          );
        })}

        {/* Verlauf-polyline — kräftiger stroke für ablesbarkeit auf sunken-bg */}
        <polyline
          points={polyline}
          fill="none"
          stroke="var(--color-text-primary)"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Datenpunkte mit heat-farb-kodierung + klartext-tooltip */}
        {points.map((p, i) => {
          const dateLabel = tooltipDateFormatter.format(new Date(p.date));
          return (
            <circle
              key={`${p.date}-${i}`}
              cx={p.x}
              cy={p.y}
              r={i === peakIdx ? 5 : 3.5}
              fill={LEVEL_FILL[p.level]}
              stroke="var(--color-surface-sunken)"
              strokeWidth={2}
            >
              <title>
                {dateLabel} · Stufe {p.level} ({LEVEL_LABEL[p.level]}) ·{" "}
                {tempFormatter.format(p.temp)} °C
              </title>
            </circle>
          );
        })}

        {/* Always-on peak-annotation — kerninfo ohne hover/touch */}
        <text
          x={peakPoint.x}
          y={PAD_TOP - 10}
          textAnchor={peakAnchor}
          fontSize={12}
          fontWeight={600}
          fill={LEVEL_FILL[peakLevel]}
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          ▲ {peakDateLabel} · St. {peakLevel} · {tempFormatter.format(peak.avg_temp_c)}°
        </text>

        {/* X-achsen-datums-labels (erstes + letztes) */}
        <text
          x={PAD_LEFT}
          y={VIEW_H - 6}
          textAnchor="start"
          fontSize={12}
          fill="var(--color-text-secondary)"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {firstDate}
        </text>
        <text
          x={VIEW_W - PAD_RIGHT}
          y={VIEW_H - 6}
          textAnchor="end"
          fontSize={12}
          fill="var(--color-text-secondary)"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {lastDate}
        </text>
      </svg>
      <figcaption className="mt-2 text-xs text-[color:var(--color-text-secondary)]">
        Tagesmittel der Raumtemperatur · Punktfarbe = höchste Stufe des Tags
      </figcaption>
    </figure>
  );
}

TrendChart.displayName = "TrendChart";
